import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_SECTION_TITLE_MAX_LENGTH, SITE_SECTION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import { Description, Fieldset, Form, Label, Submit, toast } from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { StickyFormControls } from './StickyFormControls';
import { TitleAndIconField } from './TitleAndIconField';

type EditSectionGroupFormData = {
    title: string;
    icon: string | null;
};

export interface EditSiteSectionGroupFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSectionGroup: api.SiteSectionGroup;
    disabled?: boolean;
}

function getDefaultValues(siteSectionGroup: api.SiteSectionGroup): EditSectionGroupFormData {
    return {
        title: siteSectionGroup.title,
        icon: siteSectionGroup.icon ?? null,
    };
}

export function EditSiteSectionGroupForm(props: EditSiteSectionGroupFormProps) {
    const { organization, site, siteSectionGroup, disabled } = props;

    const form = useForm<EditSectionGroupFormData>({
        defaultValues: getDefaultValues(siteSectionGroup),
    });
    const onSubmit: SubmitHandler<EditSectionGroupFormData> = async (data) => {
        await toast.catch(async () => {
            const { data: newSiteSectionGroup } =
                await apiClient.siteSectionGroups.updateSiteSectionGroupById(
                    organization.id,
                    site.id,
                    siteSectionGroup.id,
                    {
                        title: data.title,
                        icon: data.icon,
                    }
                );
            toast.success('Group updated');
            form.reset(getDefaultValues(newSiteSectionGroup));
        }, 'Error while saving group');
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <Fieldset className="p-4" disabled={disabled || form.formState.isSubmitting}>
                <TitleAndIconField
                    form={form}
                    titleName="title"
                    iconName="icon"
                    minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                    maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                    autoFocus
                >
                    <Label>Title & Icon</Label>
                    <Description>
                        Give this group a clear label and pick an icon for your nav.
                    </Description>
                </TitleAndIconField>
            </Fieldset>
            <StickyFormControls>
                <Submit label="Save" disabled={disabled} />
            </StickyFormControls>
        </Form>
    );
}
